import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Activity } from 'lucide-react';

const extractNum = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'number') return isNaN(val) ? null : val;
  if (typeof val === 'object') {
    const num = val.value ?? val.val ?? val.weight ?? val.bodyFat ?? val.current ?? null;
    if (typeof num === 'number' && !isNaN(num)) return num;
    const parsedObj = parseFloat(num);
    return isNaN(parsedObj) ? null : parsedObj;
  }
  const parsed = parseFloat(val);
  return isNaN(parsed) ? null : parsed;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const formattedDate = data.fullDate
      ? data.fullDate.includes('-')
        ? data.fullDate.split('-').reverse().join('/')
        : data.fullDate
      : label;

    return (
      <div style={{
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        padding: '0.75rem 1rem',
        borderRadius: '10px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.4rem', fontWeight: 600 }}>
          {formattedDate}
        </div>
        {payload.map((entry, index) => {
          const numVal = extractNum(entry.value);
          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', marginTop: '0.25rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color }} />
              <span style={{ color: '#cbd5e1' }}>{entry.name}:</span>
              <span style={{ fontWeight: 700, color: '#f8fafc' }}>
                {numVal != null ? numVal : '--'} {entry.dataKey === 'weight' ? 'kg' : '%'}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

const BodyCompositionChart = ({ weightHistory, fatHistory }) => {
  const hasData = (weightHistory && weightHistory.length > 0) || (fatHistory && fatHistory.length > 0);

  if (!hasData) {
    return (
      <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1.5rem' }}>
        <h3 className="text-sm font-semibold" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0', opacity: 0.8 }}>
          <Activity size={16} style={{ color: 'var(--health-cyan)' }} />
          Tendencia de Peso y Grasa Corporal
        </h3>
        <div style={{ height: '240px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
          <span className="text-muted text-sm" style={{ fontWeight: 500 }}>Sin registros de peso o composición corporal para este atleta</span>
        </div>
      </div>
    );
  }

  // Agrupación mensual para cálculo estricto de la tendencia
  const wHistory = weightHistory || [];
  const fHistory = fatHistory || [];
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const dateMap = new Map();

  wHistory.forEach(w => {
    const dStr = typeof w === 'object' ? w?.date : null;
    if (dStr) {
      if (!dateMap.has(dStr)) dateMap.set(dStr, {});
      const val = extractNum(w);
      if (val != null) dateMap.get(dStr).weight = val;
    }
  });

  fHistory.forEach(f => {
    const dStr = typeof f === 'object' ? f?.date : null;
    if (dStr) {
      if (!dateMap.has(dStr)) dateMap.set(dStr, {});
      const val = extractNum(f);
      if (val != null) dateMap.get(dStr).bodyFat = val;
    }
  });

  const monthlyMap = {};
  let refYear = null;
  let refMonth = null;
  let minYear = null;
  let minMonth = null;

  dateMap.forEach((data, dateStr) => {
    if (dateStr && dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length >= 2) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1; // 0-indexed
        if (!isNaN(y) && !isNaN(m)) {
          const monthKey = `${y}-${String(m + 1).padStart(2, '0')}`;

          if (!monthlyMap[monthKey]) {
            monthlyMap[monthKey] = { weights: [], fats: [], year: y, month: m };
          }
          if (data.weight != null && !isNaN(data.weight)) monthlyMap[monthKey].weights.push(data.weight);
          if (data.bodyFat != null && !isNaN(data.bodyFat)) monthlyMap[monthKey].fats.push(data.bodyFat);

          if (refYear === null || y > refYear || (y === refYear && m > refMonth)) {
            refYear = y;
            refMonth = m;
          }
          if (minYear === null || y < minYear || (y === minYear && m < minMonth)) {
            minYear = y;
            minMonth = m;
          }
        }
      }
    }
  });

  if (refYear === null) {
    const now = new Date();
    refYear = now.getFullYear();
    refMonth = now.getMonth();
    minYear = refYear;
    minMonth = refMonth;
  }

  // Ventana de tendencia: al menos 6 meses, y hasta 12 meses si hay historial disponible
  const monthsDiff = minYear !== null 
    ? (refYear - minYear) * 12 + (refMonth - minMonth) + 1 
    : 6;
  const numMonths = Math.max(6, Math.min(12, monthsDiff));

  const trendData = [];
  for (let i = numMonths - 1; i >= 0; i--) {
    let dMonth = refMonth - i;
    let dYear = refYear;
    while (dMonth < 0) {
      dMonth += 12;
      dYear -= 1;
    }
    const monthKey = `${dYear}-${String(dMonth + 1).padStart(2, '0')}`;
    const existing = monthlyMap[monthKey];

    const avgW = existing && existing.weights.length > 0
      ? Number((existing.weights.reduce((a, b) => a + b, 0) / existing.weights.length).toFixed(1))
      : null;
    const avgF = existing && existing.fats.length > 0
      ? Number((existing.fats.reduce((a, b) => a + b, 0) / existing.fats.length).toFixed(1))
      : null;

    trendData.push({
      fullDate: `Media Mensual (${months[dMonth]} ${dYear})`,
      date: months[dMonth],
      weight: avgW,
      bodyFat: avgF
    });
  }

  // Dynamic clean domain calculations
  const validWeights = trendData.map(d => d.weight).filter(v => v != null && !isNaN(v));
  const validFats = trendData.map(d => d.bodyFat).filter(v => v != null && !isNaN(v));

  const minW = validWeights.length ? Math.min(...validWeights) : 70;
  const maxW = validWeights.length ? Math.max(...validWeights) : 80;
  const weightMargin = Math.max(1, (maxW - minW) * 0.2);
  const weightDomain = [
    Math.floor((minW - weightMargin) * 2) / 2,
    Math.ceil((maxW + weightMargin) * 2) / 2
  ];

  const minF = validFats.length ? Math.min(...validFats) : 15;
  const maxF = validFats.length ? Math.max(...validFats) : 25;
  const fatMargin = Math.max(1, (maxF - minF) * 0.2);
  const fatDomain = [
    Math.floor((minF - fatMargin) * 2) / 2,
    Math.ceil((maxF + fatMargin) * 2) / 2
  ];

  return (
    <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h3 className="text-sm font-semibold" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0', opacity: 0.9 }}>
          <Activity size={16} style={{ color: 'var(--health-cyan)' }} />
          Tendencia de Peso y Grasa Corporal
        </h3>

        {/* Leyenda de la Tendencia */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#3b82f6', fontWeight: 600 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3b82f6', display: 'inline-block' }} />
            Peso (kg)
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#ef4444', fontWeight: 600 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }} />
            Grasa (%)
          </span>
        </div>
      </div>

      <div style={{ height: '240px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
          <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorFat" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" fontSize={11} tickMargin={10} minTickGap={15} />
            <YAxis 
              yAxisId="left" 
              domain={weightDomain} 
              stroke="#3b82f6" 
              fontSize={11} 
              tickFormatter={(tick) => `${Number(tick).toFixed(1)} kg`} 
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              domain={fatDomain} 
              stroke="#ef4444" 
              fontSize={11} 
              tickFormatter={(tick) => `${Number(tick).toFixed(1)}%`} 
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              yAxisId="left" 
              type="monotone" 
              dataKey="weight" 
              name="Peso" 
              stroke="#3b82f6" 
              strokeWidth={2.5} 
              fill="url(#colorWeight)" 
              connectNulls={true}
              dot={{ r: 4, fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 1.5 }}
              activeDot={{ r: 6, fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 2 }} 
            />
            <Area 
              yAxisId="right" 
              type="monotone" 
              dataKey="bodyFat" 
              name="Grasa Corporal" 
              stroke="#ef4444" 
              strokeWidth={2.5} 
              fill="url(#colorFat)" 
              connectNulls={true}
              dot={{ r: 4, fill: '#ef4444', stroke: '#ffffff', strokeWidth: 1.5 }}
              activeDot={{ r: 6, fill: '#ef4444', stroke: '#ffffff', strokeWidth: 2 }} 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BodyCompositionChart;
